import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rewardApi } from '../../api/reward.api';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Pencil, Check, X } from 'lucide-react';

interface RewardTypeLabelItem {
  id: number;
  rewardType: string;
  label: string;
}

export const RewardTypeLabelPanel = () => {
  const queryClient = useQueryClient();
  const [editingType, setEditingType] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const { data: labels, isLoading } = useQuery<RewardTypeLabelItem[]>({
    queryKey: ['rewardTypeLabels'],
    queryFn: () => rewardApi.getLabels().then(res => res.data.results),
  });

  const updateMutation = useMutation({
    mutationFn: (params: { rewardType: string; label: string }) =>
      rewardApi.updateLabel(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rewardTypeLabels'] });
      setEditingType(null);
    },
  });

  const startEdit = (item: RewardTypeLabelItem) => {
    setEditingType(item.rewardType);
    setEditValue(item.label);
  };

  const saveEdit = (rewardType: string) => {
    if (!editValue.trim()) return;
    updateMutation.mutate({ rewardType, label: editValue.trim() });
  };

  const cancelEdit = () => {
    setEditingType(null);
    setEditValue('');
  };

  if (isLoading) return <div className="text-center py-8 text-gray-500">로딩 중...</div>;

  return (
    <Card>
      <div className="p-5">
        <h3 className="text-base font-bold text-gray-900 mb-1">지급 내역 라벨 관리</h3>
        <p className="text-xs text-gray-500 mb-4">사용자 지급 내역에 표시되는 라벨을 수정합니다.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="py-2 pr-4 font-medium">타입 (enum)</th>
                <th className="py-2 pr-4 font-medium">표시 라벨</th>
                <th className="py-2 w-20 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {labels?.map((item) => (
                <tr key={item.rewardType} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="py-2.5 pr-4">
                    <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                      {item.rewardType}
                    </code>
                  </td>
                  <td className="py-2.5 pr-4">
                    {editingType === item.rewardType ? (
                      <input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="h-8 text-sm border rounded px-2 w-full"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit(item.rewardType);
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        autoFocus
                      />
                    ) : (
                      <span className="text-gray-900">{item.label}</span>
                    )}
                  </td>
                  <td className="py-2.5">
                    {editingType === item.rewardType ? (
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => saveEdit(item.rewardType)}
                          disabled={updateMutation.isPending}
                        >
                          <Check className="w-3.5 h-3.5 text-green-600" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={cancelEdit}>
                          <X className="w-3.5 h-3.5 text-gray-400" />
                        </Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => startEdit(item)}>
                        <Pencil className="w-3.5 h-3.5 text-gray-400" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
};
